import { useEffect } from "react";
import { ResponseError } from "@wingspanhq/wingspan-api-sdk";
import {
  WSInfiniteQueryConfig,
  WSInfiniteQueryResult,
  WSQueryKey
} from "@ws-react-query";
import {
  createV3PaginatedQueryHook,
  V3PaginatedQueryHookFor
} from "./createV3PaginatedQueryHook";
import { V3QueryMethodName } from "./createV3QueryHook";

/**
 * Build a "fetch the whole list" React Query hook from an SDK API instance + a
 * paginated `list*` method name. The eager sibling of
 * {@link createV3PaginatedQueryHook}: it is the *same* paginated query — same `queryFn`,
 * same `getNextPageParam`, and crucially the **same cache key**, so it shares one
 * cache entry with the on-demand `usePaginatedQuery` — but it walks the v3 cursor to
 * completion automatically, requesting the next page as soon as one resolves until
 * `hasNextPage` is false.
 *
 * The result is the same shape as {@link V3PaginatedQueryHook}'s (a
 * `WSInfiniteQueryResult` with all pages in `data.pages` and `flatData`), with two
 * differences: every page is loaded without the caller driving `fetchNextPage`, and
 * the whole walk is presented as **one** load — `isLoading`/`isFetching` stay true
 * and `isSuccess` stays false until the final page arrives, so they don't flicker
 * between pages and a consumer that renders on success (e.g. `WSQueries`) shows the
 * complete list at once instead of a partial list that grows page by page. Because
 * the cache is shared, pages either hook has already fetched are reused, and a
 * single `setQueryData` / invalidation on the paginated key affects both. A page error
 * stops the walk and surfaces as `isError` (no retry loop).
 *
 * Use it when a screen needs the complete collection (totals, selects, client-side
 * filtering) rather than incremental scroll. Read the whole list off `query.flatData`
 * (the SDK list items, flattened across pages) instead of walking `data.pages`.
 *
 * @param api        An instantiated SDK API client, e.g. `accountsApi`.
 * @param methodName A paginated `list*` method on `api`.
 * @param queryKey   The cache key base — pass the SAME base as the on-demand
 *                   paginated hook so the two share a cache entry.
 */
export function createV3QueryAllHook<
  TApi extends object,
  TName extends V3QueryMethodName<TApi>
>(
  api: TApi,
  methodName: TName,
  queryKey: WSQueryKey
): V3PaginatedQueryHookFor<TApi[TName]> {
  // Same paginated query (and thus same cache key) the on-demand hook is built on.
  const usePaginated = createV3PaginatedQueryHook<any, any>(
    api,
    methodName,
    queryKey
  );

  const useGeneratedQueryAll = (
    request?: unknown,
    config?: WSInfiniteQueryConfig<unknown, ResponseError>
  ) => {
    const query = usePaginated(request, config);

    const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = query;
    // Walk the cursor to completion: pull the next page as soon as the current
    // one settles, until there is none left. A no-op once fully loaded, disabled,
    // or mid-flight, so it never double-fetches. Stop on error so a failing page
    // can't re-trigger the fetch in a loop (`hasNextPage` stays true on failure).
    useEffect(() => {
      if (hasNextPage && !isFetchingNextPage && !isError) {
        void fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

    // Present the cursor walk as one load instead of leaking per-page states.
    // react-query flips to success the moment page 1 lands (`isSuccess` true,
    // `data` = just page 1) and dips `isFetching` to false in the gap before the
    // next page is requested. While more pages remain, keep reporting loading and
    // withhold success so `isLoading`/`isFetching`/`isSuccess` don't flicker;
    // `data`/`flatData` stay populated for anyone reading the partial result.
    if (query.isSuccess && hasNextPage) {
      return {
        ...query,
        status: "loading",
        fetchStatus: "fetching",
        isLoading: true,
        isInitialLoading: true,
        isSuccess: false,
        isFetching: true
        // Deliberately a non-standard react-query state (loading flags with data
        // present), so cast past the discriminated-union variants.
      } as unknown as WSInfiniteQueryResult<unknown, ResponseError>;
    }

    return query;
  };

  return useGeneratedQueryAll as unknown as V3PaginatedQueryHookFor<
    TApi[TName]
  >;
}
